import { useState, useCallback } from 'react';
import { useGoogleDriveAuth } from '../../hooks/useGoogleDriveAuth';
import { uploadFile } from '../../services/googleDriveService';
import { serializeBudgetData } from '../../services/exportService';
import { DialogShell } from './DialogShell';

interface GoogleDriveSaveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

type DialogStep = 'auth' | 'confirm' | 'uploading' | 'done';

export function GoogleDriveSaveDialog({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: GoogleDriveSaveDialogProps) {
  const {
    isAuthenticated,
    isAuthenticating,
    error: authError,
    authenticate,
  } = useGoogleDriveAuth();
  const [step, setStep] = useState<DialogStep>(() =>
    isAuthenticated ? 'confirm' : 'auth'
  );
  const [filename, setFilename] = useState(
    () => `budgeter-${new Date().toISOString().slice(0, 10)}.json`
  );

  const resetAndClose = useCallback(() => {
    setStep(isAuthenticated ? 'confirm' : 'auth');
    setFilename(`budgeter-${new Date().toISOString().slice(0, 10)}.json`);
    onClose();
  }, [onClose, isAuthenticated]);

  const handleAuth = useCallback(async () => {
    const success = await authenticate();
    if (success) {
      setStep('confirm');
    }
  }, [authenticate]);

  const handleUpload = useCallback(async () => {
    setStep('uploading');

    try {
      const data = serializeBudgetData();
      const jsonContent = JSON.stringify(data, null, 2);
      const result = await uploadFile(jsonContent, filename);

      if (result.ok) {
        setStep('done');
        onSuccess(`Guardado en Drive: ${result.data.name}`);
        resetAndClose();
      } else {
        onError(result.error);
        setStep('confirm');
      }
    } catch {
      onError('Error al guardar en Drive');
      setStep('confirm');
    }
  }, [filename, onSuccess, onError, resetAndClose]);

  const currentStep = isAuthenticated ? step : 'auth';

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={resetAndClose}
      titleId="drive-save-dialog-title"
      title="Guardar en Google Drive"
    >
      {currentStep === 'auth' && (
        <div className="space-y-4">
          <p className="text-mid-gray">
            Inicia sesión con Google para guardar tus datos en Drive.
          </p>
          {authError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-700">{authError}</p>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 text-mid-gray hover:text-near-black transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleAuth}
              disabled={isAuthenticating}
              className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50"
            >
              {isAuthenticating ? 'Conectando...' : 'Conectar con Google'}
            </button>
          </div>
        </div>
      )}

      {currentStep === 'confirm' && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="drive-filename"
              className="block text-sm text-mid-gray mb-1"
            >
              Nombre del archivo:
            </label>
            <input
              id="drive-filename"
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary"
            />
          </div>
          <p className="text-sm text-mid-gray">
            Se guardará en la carpeta &quot;Budgeter&quot; de tu Drive.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={resetAndClose}
              className="px-4 py-2 text-mid-gray hover:text-near-black transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={!filename.trim()}
              className="px-4 py-2 bg-brand-primary text-white rounded hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar
            </button>
          </div>
        </div>
      )}

      {currentStep === 'uploading' && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          <span className="ml-3 text-mid-gray">Subiendo a Drive...</span>
        </div>
      )}
    </DialogShell>
  );
}
